// Message Input Component - Text input for sending messages
import React, { useState, useRef, useEffect } from 'react';
import { Send, Paperclip, Smile } from 'lucide-react';
import VoiceInputButton from '../VoiceInputButton';

interface MessageInputProps {
  onSendMessage: (content: string) => void;
  onTyping?: (isTyping: boolean) => void;
  disabled?: boolean;
  placeholder?: string;
}

const MessageInput: React.FC<MessageInputProps> = ({
  onSendMessage,
  onTyping,
  disabled = false,
  placeholder = 'Type a message...'
}) => {
  const [message, setMessage] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const newHeight = Math.min(textareaRef.current.scrollHeight, 120);
      textareaRef.current.style.height = `${newHeight}px`;
    }
  }, [message]);

  // Handle typing indicator
  const handleTyping = () => {
    if (onTyping) {
      onTyping(true);

      // Clear existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      // Set typing to false after 2 seconds of no typing
      typingTimeoutRef.current = setTimeout(() => {
        onTyping(false);
      }, 2000);
    }
  };

  // Cleanup typing timeout on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
    handleTyping();
  };

  const handleSend = () => {
    const trimmedMessage = message.trim();
    if (trimmedMessage && !disabled) {
      onSendMessage(trimmedMessage);
      setMessage('');

      // Stop typing indicator
      if (onTyping) {
        onTyping(false);
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
        }
      }

      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Send on Enter (without Shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const canSend = message.trim().length > 0 && !disabled;

  return (
    <div
      style={{
        padding: '12px 16px',
        backgroundColor: '#1e293b',
        borderTop: '1px solid #334155'
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: '8px',
          backgroundColor: '#0f172a',
          borderRadius: '12px',
          border: isFocused ? '1px solid #3b82f6' : '1px solid #334155',
          padding: '8px 12px',
          transition: 'border-color 0.15s ease'
        }}
      >
        {/* Attachment Button (placeholder for future) */}
        <button
          type="button"
          aria-label="Attach file"
          disabled={disabled}
          style={{
            background: 'none',
            border: 'none',
            padding: '6px',
            cursor: disabled ? 'not-allowed' : 'pointer',
            color: disabled ? '#475569' : '#64748b',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'color 0.15s ease'
          }}
          onMouseEnter={(e) => {
            if (!disabled) {
              e.currentTarget.style.color = '#94a3b8';
            }
          }}
          onMouseLeave={(e) => {
            if (!disabled) {
              e.currentTarget.style.color = '#64748b';
            }
          }}
        >
          <Paperclip size={20} />
        </button>

        {/* Text Input */}
        <textarea
          ref={textareaRef}
          value={message}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={placeholder}
          disabled={disabled}
          aria-label="Message input"
          rows={1}
          style={{
            flex: 1,
            backgroundColor: 'transparent',
            border: 'none',
            color: '#f8fafc',
            fontSize: '14px',
            lineHeight: '1.5',
            resize: 'none',
            outline: 'none',
            padding: '6px 0',
            minHeight: '24px',
            maxHeight: '120px',
            fontFamily: 'inherit'
          }}
        />

        {/* Voice Input — speak to type */}
        <VoiceInputButton
          onResult={(text) => setMessage(prev => prev ? prev + ' ' + text : text)}
          size={18}
          style={{
            border: 'none',
            backgroundColor: 'transparent',
            width: '32px',
            height: '32px',
          }}
        />

        {/* Emoji Button (placeholder for future) */}
        <button
          type="button"
          aria-label="Add emoji"
          disabled={disabled}
          style={{
            background: 'none',
            border: 'none',
            padding: '6px',
            cursor: disabled ? 'not-allowed' : 'pointer',
            color: disabled ? '#475569' : '#64748b',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'color 0.15s ease'
          }}
          onMouseEnter={(e) => {
            if (!disabled) {
              e.currentTarget.style.color = '#94a3b8';
            }
          }}
          onMouseLeave={(e) => {
            if (!disabled) {
              e.currentTarget.style.color = '#64748b';
            }
          }}
        >
          <Smile size={20} />
        </button>

        {/* Send Button */}
        <button
          type="button"
          onClick={handleSend}
          disabled={!canSend}
          aria-label="Send message"
          style={{
            backgroundColor: canSend ? '#3b82f6' : '#334155',
            border: 'none',
            padding: '8px',
            borderRadius: '8px',
            cursor: canSend ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background-color 0.15s ease, transform 0.1s ease'
          }}
          onMouseEnter={(e) => {
            if (canSend) {
              e.currentTarget.style.backgroundColor = '#2563eb';
            }
          }}
          onMouseLeave={(e) => {
            if (canSend) {
              e.currentTarget.style.backgroundColor = '#3b82f6';
            }
          }}
          onMouseDown={(e) => {
            if (canSend) {
              e.currentTarget.style.transform = 'scale(0.95)';
            }
          }}
          onMouseUp={(e) => {
            if (canSend) {
              e.currentTarget.style.transform = 'scale(1)';
            }
          }}
        >
          <Send
            size={18}
            color={canSend ? '#ffffff' : '#64748b'}
            style={{ marginLeft: '2px' }}
          />
        </button>
      </div>

      {/* Helper Text */}
      <div
        style={{
          marginTop: '6px',
          fontSize: '11px',
          color: '#64748b',
          textAlign: 'right'
        }}
      >
        Press Enter to send, Shift+Enter for new line
      </div>
    </div>
  );
};

export default MessageInput;
